import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Question from './Question';
import Result from './Result';
import AchievementNotification from './AchievementNotification';
import questionsData from '../data/json/combined_fin_vocab.json';
import { addToReview, removeFromReview, updateReviewRecord, getReviewQuestions } from '../utils/reviewUtils';
import { 
    updateQuizStats, 
    updateReviewStats, 
    updateStreak, 
    checkAchievements,
    initializeAchievements 
} from '../utils/achievementUtils';

const QuizContainer = () => {
    const { category, partOfSpeech, difficulty, range } = useParams(); // URLパラメータから情報を取得
    const [selectedQuestions, setSelectedQuestions] = useState([]); // クイズに使用する質問を保持
    const [currentQuestion, setCurrentQuestion] = useState(0); // 現在の質問のインデックス
    const [score, setScore] = useState(0); // スコアを保持
    const [isFinished, setIsFinished] = useState(false); // クイズが終了したかどうかを保持
    const [answers, setAnswers] = useState([]); // ユーザーの回答を保持
    const [isLoading, setIsLoading] = useState(true); // 質問を読み込んでいるかどうかを保持
    const [newAchievements, setNewAchievements] = useState([]); // 新しく解除された実績
    const [currentAchievementIndex, setCurrentAchievementIndex] = useState(0); // 現在表示中の実績のインデックス

    useEffect(() => {
        // 実績システムの初期化
        initializeAchievements();
        
        // 指定された品詞、難易度、範囲に基づいて質問を取得
        const getQuestions = (data, partOfSpeech, difficulty, range, category) => {
            // 復習モードの場合
            if (range === 'review') {
                return getReviewQuestions();
            }
            
            let filteredQuestions = [];
            if(category){
                filteredQuestions = data.filter(
                    item => item.category === category
                )
            }else{
                filteredQuestions = data.filter(
                    item => item.part_of_speech === partOfSpeech && item.difficulty === difficulty
                );
            }
            const startIndex = (range - 1) * 10;
            const endIndex = startIndex + 10;
            return filteredQuestions.slice(startIndex, endIndex);
        };

        // 質問をセット
        const questionsForQuiz = getQuestions(questionsData, partOfSpeech, difficulty, range, category);
        setSelectedQuestions(questionsForQuiz);
        setIsLoading(false);
    }, [partOfSpeech, difficulty, range, category]);

    const handleAnswer = (selectedOption) => {
        const currentQuestionData = selectedQuestions[currentQuestion];
        const isCorrect = selectedOption === currentQuestionData?.japanese; // 日本語を正解とする
        
        if (isCorrect) {
            setScore(score + 1); // 正解の場合スコアを増やす
            // 正解した場合、復習リストから削除（復習モードの場合）
            if (range === 'review') {
                removeFromReview(currentQuestionData);
            }
        } else {
            // 間違えた場合、復習リストに追加
            addToReview(currentQuestionData);
        }

        // 復習モードの場合、復習記録を更新
        if (range === 'review') {
            updateReviewRecord(currentQuestionData);
        }

        // ユーザーの回答を保存
        setAnswers([
            ...answers,
            {
                question: currentQuestionData?.word, // 質問の単語
                selectedOption, // ユーザーの選択肢
                correctAnswer: currentQuestionData?.japanese, // 正しい答え
                isCorrect, // 正解かどうか
                example: currentQuestionData?.example,
            },
        ]);

        // 連続正解の更新
        updateStreak(isCorrect);

        // 次の質問に進むか、クイズを終了するかを決定
        const nextQuestion = currentQuestion + 1;
        if (nextQuestion < selectedQuestions.length) {
            setCurrentQuestion(nextQuestion);
        } else {
            // クイズ完了時の処理
            setIsFinished(true);
            
            // 統計の更新
            if (range === 'review') {
                updateReviewStats();
            } else {
                updateQuizStats(score + (isCorrect ? 1 : 0), selectedQuestions.length, category, difficulty);
            }
            
            // 実績チェック
            const unlockedAchievements = checkAchievements();
            if (unlockedAchievements.length > 0) {
                setNewAchievements(unlockedAchievements);
                setCurrentAchievementIndex(0);
            }
        }
    };

    // 質問が読み込まれている間はローディングメッセージを表示
    if (isLoading) {
        return (
            <div className="modern-container">
                <div className="modern-section text-center">
                    <div className="fade-in-up">
                        <div style={{ 
                            fontSize: '4rem',
                            marginBottom: 'var(--spacing-lg)',
                            animation: 'pulse 2s infinite'
                        }}>
                            📚
                        </div>
                        <h4 style={{ 
                            color: 'var(--text-primary)',
                            marginBottom: 'var(--spacing-md)'
                        }}>
                            問題を準備しています...
                        </h4>
                        <div style={{
                            width: '200px',
                            height: '4px',
                            background: 'var(--border-color)',
                            borderRadius: 'var(--radius-lg)',
                            margin: '0 auto',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: '50%',
                                height: '100%',
                                background: 'var(--primary-gradient)',
                                borderRadius: 'var(--radius-lg)',
                                animation: 'loading-bar 1.5s ease-in-out infinite alternate'
                            }} />
                        </div>
                    </div>
                </div>
                <style jsx>{`
                    @keyframes loading-bar {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(300%); }
                    }
                `}</style>
            </div>
        );
    }

    // 質問がない場合はエラーメッセージを表示
    if (!selectedQuestions.length) {
        return (
            <div className="modern-container">
                <div className="modern-section text-center">
                    <div className="modern-alert modern-alert-warning fade-in-up">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '4rem', marginBottom: 'var(--spacing-lg)' }}>
                                ⚠️
                            </div>
                            <h4>このカテゴリ・範囲には問題がありません</h4>
                            <p>別のカテゴリや範囲を選択してください。</p>
                            <div style={{ 
                                display: 'flex',
                                gap: 'var(--spacing-md)',
                                justifyContent: 'center',
                                flexWrap: 'wrap'
                            }}>
                                <button 
                                    onClick={() => window.history.back()}
                                    className="modern-btn modern-btn-secondary"
                                >
                                    戻る
                                </button>
                                <a href="/" className="modern-btn modern-btn-primary">
                                    ホームに戻る
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 実績通知のハンドラ
    const handleAchievementClose = () => {
        const nextIndex = currentAchievementIndex + 1;
        if (nextIndex < newAchievements.length) {
            setCurrentAchievementIndex(nextIndex);
        } else {
            setNewAchievements([]);
            setCurrentAchievementIndex(0);
        }
    };

    // クイズの結果を表示するか、次の質問を表示するかを決定
    return (
        <>
            <Container className="mt-5">
                {isFinished ? (
                    <Result score={score} total={selectedQuestions.length} answers={answers} /> // クイズが終了したら結果を表示
                ) : (
                    <Question
                        question={selectedQuestions[currentQuestion]} // 現在の質問
                        handleAnswer={handleAnswer} // ユーザーが選択肢を選んだときの処理
                        currentQuestion={currentQuestion + 1} // 現在の質問番号
                        totalQuestions={selectedQuestions.length} // 全質問数
                        allQuestions={questionsData} // 全ての質問データ
                    />
                )}
            </Container>
            
            {/* 実績通知 */}
            {newAchievements.length > 0 && (
                <AchievementNotification
                    achievement={newAchievements[currentAchievementIndex]}
                    onClose={handleAchievementClose}
                />
            )}
        </>
    );
}


export default QuizContainer;